/**
 * 统一的表达式引擎
 * 支持在 Schema 配置中使用表达式字符串，实现动态值计算和字段联动
 *
 * 表达式格式：{{ expression }}
 * 支持的变量：
 * - $self: 当前字段的值
 * - $form: 当前所有字段的值（对象形式）
 * - $values: 当前所有字段的值（与 $form 相同，提供别名）
 * - $deps: 依赖字段的值列表（数组形式，用于联动场景）
 * - $env: 环境信息（平台/产品等）
 */

import { getPid, getPlatform, getProductId, getProductName, getTagCapitalPlatform } from '../product-info';
import { isHtmlString, parseHtmlString } from './template-parser';

/**
 * 表达式上下文
 * 所有变量都是可选的，根据使用场景提供相应的变量
 */
export interface ExpressionContext {
  /**
   * 当前字段的值
   */
  $self?: any;
  /**
   * 所有字段的值（对象形式）
   */
  $form?: Record<string, any>;
  /**
   * 所有字段的值（与 $form 相同，提供别名）
   */
  $values?: Record<string, any>;
  /**
   * 依赖字段的值列表（数组形式）
   * $deps[0] 对应 dependencies[0] 的值
   */
  $deps?: any[];
  /**
   * 环境信息（平台/产品等）
   */
  $env?: {
    platformId?: string;
    platform?: string;
    platformName?: string;
    productId?: string;
    productName?: string;
  };
}

/**
 * 计算表达式的值
 * 支持 {{ expression }} 格式的表达式字符串
 *
 * @param expression 表达式字符串或普通值
 * @param context 表达式上下文
 * @returns 计算后的值
 *
 * @example
 * // 隐藏字段：当当前字段值为空时隐藏
 * evaluateExpression('{{ !$self }}', { $self: '', $form: {} })
 * // => true
 *
 * // 联动显示：当其他字段值为特定值时显示
 * evaluateExpression('{{ $form.fieldName !== "value" }}', {
 *   $self: null,
 *   $form: { fieldName: 'value' }
 * })
 * // => false
 *
 * // 初始化值：根据其他字段计算初始值
 * evaluateExpression('{{ $form.fieldA + $form.fieldB }}', {
 *   $self: null,
 *   $form: { fieldA: 1, fieldB: 2 }
 * })
 * // => 3
 *
 * // 使用 $deps（联动场景）
 * evaluateExpression('{{ !$deps[0] }}', {
 *   $deps: [true],
 *   $self: false,
 *   $form: {}
 * })
 * // => false
 */
export function evaluateExpression(
  expression: any,
  context: ExpressionContext = {},
): any {
  // 如果不是字符串，直接返回
  if (typeof expression !== 'string') {
    return expression;
  }

  // 检查是否是表达式字符串（被 {{ }} 包裹）
  const templateMatch = expression.match(/^\{\{(.+)\}\}$/);
  if (!templateMatch) {
    // 不是表达式，直接返回原值
    return expression;
  }

  try {
    const code = templateMatch[1].trim();

    // 从上下文中提取变量（提供默认值以避免 undefined）
    const {
      $self = undefined,
      $form = {},
      $values = $form, // $values 默认等于 $form
      $deps = [],
      $env = {},
    } = context;

    // 使用 Function 构造函数安全执行表达式
    // 创建一个函数，接受所有可能的变量作为参数
    // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
    const func = new Function(
      '$self',
      '$form',
      '$values',
      '$deps',
      '$env',
      `return ${code}`,
    );

    return func($self, $form, $values, $deps, $env);
  } catch (e) {
    console.error('[ExpressionEngine] Failed to evaluate expression:', expression, e);
    // 表达式执行失败时，返回原值
    return expression;
  }
}

/**
 * 从嵌套对象中获取值（支持点号路径）
 *
 * @example
 * getNestedValue({ a: { b: { c: 1 } } }, 'a.b.c') // => 1
 * getNestedValue({ a: { b: { c: 1 } } }, 'a.b') // => { c: 1 }
 */
export function getNestedValue(obj: Record<string, any>, path: string): any {
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    value = value[key];
  }
  return value;
}

/**
 * 创建表达式上下文（用于 tds-form-next）
 *
 * @param fieldName 当前字段名
 * @param allValues 所有字段的值
 * @returns 表达式上下文
 */
export function createExpressionContext(
  fieldName: string,
  allValues: Record<string, any>,
): ExpressionContext {
  const $self = getNestedValue(allValues, fieldName);
  const $env = {
    platformId: getPid(),
    platform: getPlatform(),
    platformName: getTagCapitalPlatform(),
    productId: getProductId(),
    productName: getProductName(),
  };

  return {
    $self,
    $form: allValues,
    $values: allValues, // $values 是 $form 的别名
    $env,
  };
}

/**
 * 创建联动表达式上下文（用于 tds-schema-form）
 *
 * @param fieldName 当前字段名
 * @param dependencies 依赖字段名列表
 * @param allValues 所有字段的值
 * @returns 表达式上下文
 */
export function createReactionExpressionContext(
  fieldName: string,
  dependencies: string[],
  allValues: Record<string, any>,
): ExpressionContext {
  const $self = getNestedValue(allValues, fieldName);
  const $deps = dependencies.map(depName => getNestedValue(allValues, depName));
  const $env = {
    platformId: getPid(),
    platform: getPlatform(),
    platformName: getTagCapitalPlatform(),
    productId: getProductId(),
    productName: getProductName(),
  };

  return {
    $self,
    $form: allValues,
    $values: allValues,
    $deps,
    $env,
  };
}

/**
 * 递归解析对象/数组中的所有表达式字符串
 * 遍历整个数据结构，查找所有包含 {{}} 格式的字符串值并解析它们
 *
 * @param value 要处理的值（可以是对象、数组、字符串等）
 * @param context 表达式上下文
 * @param visited 用于防止循环引用的 Set（内部使用）
 * @returns 处理后的值
 */
export function parseExpressionsRecursively(
  value: any,
  context: ExpressionContext,
  visited: WeakSet<any> = new WeakSet(),
): any {
  // 处理 null 和 undefined
  if (value === null || value === undefined) {
    return value;
  }

  // 处理字符串：检查是否是表达式字符串
  if (typeof value === 'string') {
    const evaluated = evaluateExpression(value, context);

    if (typeof evaluated === 'string' && isHtmlString(evaluated)) {
      const parsed = parseHtmlString(evaluated);
      return parsed ?? evaluated;
    }

    return evaluated;
  }

  // 处理数组：递归处理每个元素
  if (Array.isArray(value)) {
    return value.map(item => parseExpressionsRecursively(item, context, visited));
  }

  // 处理对象：递归处理每个属性
  if (typeof value === 'object') {
    // 防止循环引用
    if (visited.has(value)) {
      return value;
    }
    visited.add(value);

    const result: any = {};
    Object.keys(value).forEach((key) => {
      result[key] = parseExpressionsRecursively(value[key], context, visited);
    });
    return result;
  }

  // 其他类型（number, boolean, function 等）直接返回
  return value;
}

